import { z } from 'zod/v4';

/**
 * `PATCH /api/audit-findings/bulk-remove` body: the caller's findings to
 * soft-delete, all or none. Same shape as the legacy
 * `DELETE /v1/audit-findings/bulk-remove`, whose schema (at least one id, no
 * duplicates) was declared but never wired — a missing `ids` was a 500, an
 * empty list a silent no-op and a duplicate a 404. They are 400s now.
 */
export const BulkRemoveAuditFindingsBodySchema = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .refine(ids => new Set(ids).size === ids.length, {
      message: 'ids must be unique',
    }),
});

export type BulkRemoveAuditFindingsBody = z.infer<
  typeof BulkRemoveAuditFindingsBodySchema
>;

/** An optional text field: an empty string is stored as NULL. */
const optionalText = (max?: number) =>
  (max ? z.string().max(max) : z.string()).nullable().optional();

/**
 * `POST /api/audit-findings` body — a finding the caller's company records on
 * an audit it can open. Same fields as the legacy `POST /v1/audit-findings`,
 * capped at the column sizes. The legacy schema rejected an empty optional
 * field; it is stored as NULL now. Unknown keys (the dashboard also sends
 * `rating`) are stripped, as before.
 */
export const CreateAuditFindingBodySchema = z.object({
  auditId: z.string().min(1).max(36),
  title: z.string().min(1).max(4000),
  description: optionalText(),
  urgency: optionalText(255),
  impactAreaId: optionalText(255),
});

export type CreateAuditFindingBody = z.infer<
  typeof CreateAuditFindingBodySchema
>;

/** A finding as stored, answered by `POST /api/audit-findings`. */
export const AuditFindingRowSchema = z.object({
  id: z.string(),
  auditId: z.string().nullable(),
  companyId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  urgency: z.string().nullable(),
  impactAreaId: z.string().nullable(),
  source: z.string().nullable(),
  type: z.string().nullable(),
  isRemoved: z.boolean(),
  createdAt: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  updatedAt: z.string().nullable(),
  updatedByUserId: z.string().nullable(),
});

export type AuditFindingRow = z.infer<typeof AuditFindingRowSchema>;

/**
 * `PATCH /api/audit-findings/:id` body — any of the editable fields. Absent
 * leaves a field as it is; for the optional ones, `null` or an empty string
 * clears it. The legacy route treated both as "keep the old value" (its
 * `value || existing` fallback), so clearing a cell silently did nothing; an
 * empty or null `title` is a 400 now rather than that same no-op.
 */
export const PatchAuditFindingBodySchema = z.object({
  title: z.string().min(1).max(4000).optional(),
  description: optionalText(),
  urgency: optionalText(255),
  impactAreaId: optionalText(255),
});

export type PatchAuditFindingBody = z.infer<typeof PatchAuditFindingBodySchema>;
